import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import XKDK_ABI from 'abis/XKDK.json'
import { xKDKAbi } from 'abis/xKDKAbi'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn, ColumnCenter } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import { AutoRow, RowBetween } from 'components/Row'
import Step from 'components/StepLine/common/Step'
import { Dots } from 'components/swap/styleds'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { XKDK_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useContext, useEffect, useMemo, useState } from 'react'
import { ThemeContext } from 'styled-components'
import { Separator, ThemedText } from 'theme'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi, Hash } from 'viem'
import { Address, maxUint256 } from 'viem'
import { useReadContract } from 'wagmi'

enum ApproveUsageStepState {
  UNKNOWN = 'UNKNOWN',
  APPROVE = 'APPROVE',
  NOT_APPROVED = 'NOT_APPROVED',
  APPROVED = 'APPROVED',
}
export const useApproveUsageStep = (amount?: CurrencyAmount<Token>, spender?: string): Step => {
  const { account, chainId, wagmiPublicClient } = useConnection()
  const xKDKAddress = XKDK_ADDRESSES[chainId]
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const { data: usageApprovalsResult } = useReadContract({
    address: xKDKAddress,
    abi: xKDKAbi,
    functionName: 'usageApprovals',
    args: [account as Address, spender as Address],
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!xKDKAddress && !!account && !!spender,
    },
  })

  const usage = usageApprovalsResult
  const [approveState, setApproveState] = useState<ApproveUsageStepState>(ApproveUsageStepState.UNKNOWN)
  const [approving, setApproving] = useState<boolean>(false)
  const [txHash, setTxHash] = useState<Hash | undefined>()
  const [approvalConfirmed, setApprovalConfirmed] = useState(false)
  const { writeContractAsync } = useWriteContract()

  const approvalAmount = useMemo(() => {
    return amount ? BigInt(amount.quotient.toString()) : BigInt(0)
  }, [amount])

  const hasSufficientUsage = useMemo(() => {
    return amount !== undefined && usage !== undefined && usage >= approvalAmount
  }, [amount, usage, approvalAmount])

  const approve = async () => {
    if (!xKDKAddress || !amount || !spender || !wagmiPublicClient) return
    try {
      setApproveState(ApproveUsageStepState.APPROVE)
      setApproving(true)

      // Estimate gas using BigInt
      const gas = await wagmiPublicClient.estimateContractGas({
        address: xKDKAddress,
        abi: XKDK_ABI,
        functionName: 'approveUsage',
        args: [spender as Address, maxUint256],
        account,
      })

      const hash = await writeContractAsync({
        address: xKDKAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'approveUsage',
        args: [spender as Address, maxUint256],
        gas: calculateGasMargin(gas),
      })
      setTxHash(hash as Hash)
    } catch (e) {
      console.error('Error during approveUsage:', e)
      setApproveState(ApproveUsageStepState.NOT_APPROVED)
    } finally {
      setApproving(false)
    }
  }

  useEffect(() => {
    // Removed debug log for production
    if (hasSufficientUsage || approvalConfirmed) {
      setApproveState(ApproveUsageStepState.APPROVED)
    } else if (txHash) {
      setApproveState(ApproveUsageStepState.APPROVE)
    } else if (!approving && usage !== undefined) {
      setApproveState(ApproveUsageStepState.NOT_APPROVED)
    }
  }, [hasSufficientUsage, approvalConfirmed, txHash, approving, usage])

  useEffect(() => {
    if (!txHash) return

    const waitForReceipt = async () => {
      try {
        const receipt = await waitForTransactionReceipt({
          hash: txHash,
        })

        if (receipt.status === 'success') {
          setApprovalConfirmed(true)
        } else {
          setApprovalConfirmed(false)
        }
      } catch (error) {
        console.error('Error waiting for approveUsage receipt:', error)
        setApprovalConfirmed(false)
      } finally {
        setTxHash(undefined)
      }
    }

    waitForReceipt()
  }, [txHash, waitForTransactionReceipt])

  useEffect(() => {
    setTxHash(undefined)
    setApprovalConfirmed(false)
  }, [amount?.quotient.toString(), spender])

  return {
    title: 'Approve Staking',
    condition: approveState === ApproveUsageStepState.APPROVED,
    component: <ApproveUsageStep approve={approve} amount={amount} approvalState={approveState} spender={spender} />,
  }
}

type ApproveUsageStepProps = {
  approve: () => Promise<void>
  approvalState: ApproveUsageStepState
  amount?: CurrencyAmount<Currency>
  spender?: string
}
const ApproveUsageStep = ({ approve, approvalState, amount, spender }: ApproveUsageStepProps) => {
  const theme = useContext(ThemeContext)
  const { account } = useConnection()
  const token = amount?.currency?.isToken ? amount.currency : undefined

  return (
    <ColumnCenter gap="8px">
      <LightCard>
        <AutoColumn gap="sm">
          <RowBetween>
            <AutoRow>
              <div>{amount?.toExact()}</div>
            </AutoRow>

            <AutoRow gap="2px" justify="flex-end">
              <CurrencyLogo currency={amount?.currency}></CurrencyLogo>
              <div>{amount?.currency.symbol}</div>
            </AutoRow>
          </RowBetween>
          <Separator />
          <RowBetween>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              Requested allowance
            </ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>∞</ThemedText.DeprecatedSubHeader>
          </RowBetween>
        </AutoColumn>
      </LightCard>
      <ButtonPrimary onClick={approve} disabled={![ApproveUsageStepState.NOT_APPROVED].includes(approvalState)}>
        {approvalState === ApproveUsageStepState.UNKNOWN ? (
          <ThemedText.DeprecatedSubHeader>
            Checking existing usage allowance
            <Dots />
          </ThemedText.DeprecatedSubHeader>
        ) : (
          <ThemedText.DeprecatedSubHeader>
            Approve {amount?.currency.symbol}
            {approvalState === ApproveUsageStepState.APPROVE && <Dots />}
          </ThemedText.DeprecatedSubHeader>
        )}
      </ButtonPrimary>
    </ColumnCenter>
  )
}

export default ApproveUsageStep
